import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useDriverStateV2 } from '../../hooks/useDriverStateV2';
import env from '../../config/env';

export interface CompanyInfo {
  id: string;
  name: string;
  companyPhone: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  updatedAt?: string;
}

export interface UpdateCompanyPayload {
  name?: string;
  companyPhone?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

const buildAuthHeaders = (token: string, companyId: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
  'x-company-id': companyId,
});

const getCompanyInfo = async (
  token: string,
  companyId: string,
): Promise<CompanyInfo> => {
  const response = await fetch(`${env.apiUrl}/companies/${companyId}`, {
    method: 'GET',
    headers: buildAuthHeaders(token, companyId),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch company info: ${response.statusText}`);
  }

  return response.json();
};

export const updateCompany = async (
  token: string,
  companyId: string,
  payload: UpdateCompanyPayload,
): Promise<CompanyInfo> => {
  const response = await fetch(`${env.apiUrl}/companies/${companyId}`, {
    method: 'PATCH',
    headers: buildAuthHeaders(token, companyId),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to update company: ${response.statusText}`);
  }

  return response.json();
};

export const useCompanyInfo = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  return useQuery({
    queryKey: ['company-info', driver?.companyId],
    queryFn: async () => {
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }
      const token = await getAccessTokenSilently();
      return getCompanyInfo(token, driver.companyId);
    },
    enabled: !!driver?.companyId,
    staleTime: 30 * 60 * 1000,
    refetchInterval: undefined,
  });
};

export const useUpdateCompany = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateCompanyPayload) => {
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }
      const token = await getAccessTokenSilently();
      return updateCompany(token, driver.companyId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-info', driver?.companyId] });
    },
  });
};
